// C:\IntegraNube_WebStore\src\App.js
/* ------------------------------------------------------------------
 * IntegraNube WebStore - aplicación principal resiliente
 *
 * Esta versión:
 *  - Carga GET /tienda con reintentos automáticos desde api.js.
 *  - Mantiene un loader visible mientras intenta recuperar conexión.
 *  - Evita duplicar la carga real de /tienda por React StrictMode en DEV.
 *  - Permite reintentar manualmente si se agotan los intentos.
 *  - Reintenta automáticamente cuando el navegador informa que volvió
 *    la conectividad.
 *  - Al cambiar de página, posiciona siempre la nueva vista arriba.
 *  - Desactiva la restauración automática de scroll del navegador.
 *
 * IMPORTANTE:
 *  - NO usa caché/localStorage de /tienda todavía.
 *  - El scroll sólo se reinicia cuando cambia el pathname.
 *    Cambiar filtros/query params dentro de /productos NO mueve la vista.
 * ------------------------------------------------------------------ */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';

import {
  CssBaseline,
} from '@mui/material';

import {
  createTheme,
  ThemeProvider,
} from '@mui/material/styles';

import api from './api/api';

import Header from './components/Header';
import Menu from './components/Menu';
import Footer from './components/Footer';
import SeoClient from './components/SeoClient';

import Inicio from './pages/Inicio';
import Productos from './pages/Productos';
import ProductoDetalle from './pages/ProductoDetalle';
import Carrito from './pages/Carrito';
import FinalizarCompra from './pages/FinalizarCompra';
import Contacto from './pages/Contacto';
import SeguimientoPedido from './pages/SeguimientoPedido';
import NoEncontrado from './pages/NoEncontrado';
import RetailLanding from './pages/RetailLanding';


import Categorias from './pages/Categorias';
import Categoria from './pages/Categoria';

import Marcas from './pages/Marcas';
import MarcaDetalles from './pages/MarcaDetalles';

import PaginaDinamica from './pages/PaginaDinamica';

/* =====================================================================
 * DEDUPLICACIÓN DE /tienda
 * ---------------------------------------------------------------------
 * React 18 StrictMode puede montar/desmontar/montar los efectos en DEV.
 * En vez de generar dos GET /tienda simultáneos, ambas instancias
 * comparten la misma Promise mientras la petición esté en curso.
 * =================================================================== */

let tiendaRequestInFlight = null;
const tiendaRetryListeners = new Set();

function notifyTiendaRetryListeners(info) {
  tiendaRetryListeners.forEach((listener) => {
    try {
      listener(info);
    } catch (error) {
      console.warn(
        '[WebStore] Error notificando retry de /tienda:',
        error
      );
    }
  });
}

function requestPublicStore(onRetry) {
  if (typeof onRetry === 'function') {
    tiendaRetryListeners.add(onRetry);
  }

  if (!tiendaRequestInFlight) {
    tiendaRequestInFlight = api
      .get('/tienda', {
        /*
         * /tienda es crítica para iniciar la aplicación.
         * Le damos un poco más de tolerancia que al GET normal.
         * 5 retries + intento inicial = hasta 6 intentos.
         */
        timeout: 7000,
        retries: 5,
        onRetry: notifyTiendaRetryListeners,
      })
      .finally(() => {
        tiendaRequestInFlight = null;
      });
  }

  return tiendaRequestInFlight.finally(() => {
    if (typeof onRetry === 'function') {
      tiendaRetryListeners.delete(onRetry);
    }
  });
}

/* =====================================================================
 * HELPERS UI
 * =================================================================== */

function getErrorMessage(error) {
  return (
    error?.friendlyMessage ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    'No fue posible cargar la información de la tienda.'
  );
}


function getStoreColor(
  value,
  fallback
) {
  const color =
    String(value || '').trim();

  return color || fallback;
}

function buildStoreTheme(tienda) {
  const branding =
    tienda?.branding || {};

  const colorPrimario =
    getStoreColor(
      branding.colorPrimario,
      '#1976d2'
    );

  const colorSecundario =
    getStoreColor(
      branding.colorSecundario,
      '#111827'
    );

  const colorFondo =
    getStoreColor(
      branding.colorFondo,
      '#f8fafc'
    );

  const colorSuperficie =
    getStoreColor(
      branding.colorSuperficie,
      '#ffffff'
    );

  const colorTexto =
    getStoreColor(
      branding.colorTexto,
      '#111827'
    );

  const colorTextoSecundario =
    getStoreColor(
      branding.colorTextoSecundario,
      '#6b7280'
    );

  const colorOferta =
    getStoreColor(
      branding.colorOferta,
      '#dc2626'
    );

  const colorExito =
    getStoreColor(
      branding.colorExito,
      '#059669'
    );

  const moneda =
    String(
      tienda?.regionalizacion?.moneda ||
      'CLP'
    )
      .trim()
      .toUpperCase();

  const locale =
    String(
      tienda?.regionalizacion?.locale ||
      'es-CL'
    ).trim();

  return createTheme({
    palette: {
      mode: 'light',

      primary: {
        main: colorPrimario,
      },

      secondary: {
        main: colorSecundario,
      },

      background: {
        default: colorFondo,
        paper: colorSuperficie,
      },

      text: {
        primary: colorTexto,
        secondary:
          colorTextoSecundario,
      },
    },

    /*
     * No definimos fontFamily aquí:
     * usamos la tipografía estándar del Theme MUI,
     * para que Header, Footer y páginas compartan exactamente
     * la misma familia y métricas.
     */
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor:
              colorFondo,
            color:
              colorTexto,
          },
        },
      },
    },

    /*
     * Tokens propios de IntegraNube.
     * Al ser JavaScript pueden consultarse mediante useTheme()
     * en componentes futuros.
     */
    integranube: {
      colorOferta,
      colorExito,
      moneda,
      locale,
    },
  });
}

function applyStoreDocumentData(tienda) {
  if (!tienda || typeof document === 'undefined') {
    return;
  }

  if (tienda?.seo?.titulo) {
    document.title = tienda.seo.titulo;
  } else if (tienda?.nombre) {
    document.title = tienda.nombre;
  }

  const description = tienda?.seo?.descripcion;

  if (description) {
    let meta = document.querySelector(
      'meta[name="description"]'
    );

    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }

    meta.setAttribute('content', description);
  }

  const favicon = tienda?.branding?.favicon;

  if (favicon) {
    let link = document.querySelector(
      'link[rel="icon"]'
    );

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      document.head.appendChild(link);
    }

    link.setAttribute('href', favicon);
  }

  const root = document.documentElement;

  const branding =
    tienda?.branding || {};

  const cssVariables = {
    '--store-primary-color':
      branding.colorPrimario,
    '--store-secondary-color':
      branding.colorSecundario,
    '--store-background-color':
      branding.colorFondo,
    '--store-surface-color':
      branding.colorSuperficie,
    '--store-text-color':
      branding.colorTexto,
    '--store-text-secondary-color':
      branding.colorTextoSecundario,
    '--store-offer-color':
      branding.colorOferta,
    '--store-success-color':
      branding.colorExito,
  };

  Object.entries(
    cssVariables
  ).forEach(
    ([key, value]) => {
      if (value) {
        root.style.setProperty(
          key,
          value
        );
      }
    }
  );

  const locale =
    String(
      tienda?.regionalizacion
        ?.locale ||
      ''
    ).trim();

  if (locale) {
    root.setAttribute(
      'lang',
      locale
    );
  }

  if (
    tienda?.configVersion !==
    undefined &&
    tienda?.configVersion !==
    null
  ) {
    root.setAttribute(
      'data-store-config-version',
      String(
        tienda.configVersion
      )
    );
  }
}

/* =====================================================================
 * PANTALLAS DE ESTADO
 * =================================================================== */

function LoadingStore({ retryInfo }) {
  const reconnecting =
    Boolean(retryInfo?.retryNumber);

  return (
    <div style={styles.centerScreen}>
      <style>{`
        @keyframes webstore-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={styles.loaderCard}>
        <div
          style={styles.spinner}
          aria-hidden="true"
        />

        <div style={styles.loaderTitle}>
          {reconnecting
            ? 'Reconectando con la tienda…'
            : 'Cargando tienda…'}
        </div>

        <div style={styles.loaderText}>
          {reconnecting
            ? `La conexión está inestable. Intento ${
                retryInfo.retryNumber + 1
              } en curso.`
            : 'Estamos obteniendo la información más reciente.'}
        </div>
      </div>
    </div>
  );
}

function StoreLoadError({
  message,
  onRetry,
  retrying,
}) {
  return (
    <div style={styles.centerScreen}>
      <style>{`
        @keyframes webstore-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={styles.errorCard}>
        {retrying ? (
          <div
            style={styles.spinner}
            aria-hidden="true"
          />
        ) : (
          <div
            style={styles.warningIcon}
            aria-hidden="true"
          >
            !
          </div>
        )}

        <div style={styles.loaderTitle}>
          {retrying
            ? 'Intentando nuevamente…'
            : 'No pudimos conectar con la tienda'}
        </div>

        <div style={styles.loaderText}>
          {retrying
            ? 'Estamos restableciendo la conexión.'
            : message ||
              'La conexión puede estar temporalmente inestable.'}
        </div>

        {!retrying && (
          <button
            type="button"
            onClick={onRetry}
            style={styles.retryButton}
          >
            Reintentar
          </button>
        )}
      </div>
    </div>
  );
}

/* =====================================================================
 * APP CONTENT
 * ---------------------------------------------------------------------
 * Debe vivir dentro de <Router> porque useLocation() sólo puede usarse
 * cuando ya existe el contexto de React Router.
 *
 * No se crea ningún componente ScrollToTop separado.
 * La lógica de scroll queda centralizada en este mismo App.js.
 * =================================================================== */

function StoreAppContent() {
  const location = useLocation();

  const [tienda, setTienda] =
    useState(null);

  const [storeMeta, setStoreMeta] =
    useState(null);

  const [loadingStore, setLoadingStore] =
    useState(true);

  const [storeError, setStoreError] =
    useState('');

  const [retryInfo, setRetryInfo] =
    useState(null);

  const storeTheme =
    useMemo(
      () =>
        buildStoreTheme(
          tienda
        ),
      [tienda]
    );

  /* ===================================================================
   * SCROLL GENERAL
   * -------------------------------------------------------------------
   * Al cambiar de página:
   *
   * /productos -> /slug-del-producto
   * /producto/123 -> /carrito
   * /carrito -> /
   *
   * la nueva vista comienza siempre arriba.
   *
   * Usamos pathname, no search, para que los filtros:
   *
   * /productos?marca_id=1
   * /productos?marca_id=1&precio_min=3000
   *
   * no provoquen saltos de scroll.
   * ================================================================= */

  useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });
  }, [location.pathname]);

  /*
   * Algunos navegadores intentan restaurar la posición anterior al
   * navegar con Atrás/Adelante. La WebStore administra esto manualmente.
   */
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !window.history ||
      !('scrollRestoration' in window.history)
    ) {
      return undefined;
    }

    const previousValue =
      window.history.scrollRestoration;

    window.history.scrollRestoration =
      'manual';

    return () => {
      window.history.scrollRestoration =
        previousValue;
    };
  }, []);

  /* ===================================================================
   * CARGA MANUAL / REINTENTO DE TIENDA
   * ================================================================= */

  const loadStore =
    useCallback(async () => {
      setLoadingStore(true);
      setStoreError('');
      setRetryInfo(null);

      const handleRetry = (info) => {
        setRetryInfo(info);
      };

      try {
        const response =
          await requestPublicStore(
            handleRetry
          );

        const payload =
          response?.data || {};

        if (
          payload?.status !== 'ok' ||
          !payload?.data
        ) {
          const error = new Error(
            payload?.message ||
              'La API devolvió una respuesta de tienda inválida.'
          );

          error.response = response;
          throw error;
        }

        setTienda(payload.data);
        setStoreMeta(
          payload.store || null
        );
        setStoreError('');
        setRetryInfo(null);

        applyStoreDocumentData(
          payload.data
        );
      } catch (error) {
        console.error(
          '[WebStore App] Error cargando información pública de la tienda:',
          error
        );

        setStoreError(
          getErrorMessage(error)
        );
      } finally {
        setLoadingStore(false);
      }
    }, []);

  /* ===================================================================
   * CARGA INICIAL DE TIENDA
   * ================================================================= */

  useEffect(() => {
    let mounted = true;

    const handleRetry = (info) => {
      if (mounted) {
        setRetryInfo(info);
      }
    };

    const run = async () => {
      setLoadingStore(true);
      setStoreError('');
      setRetryInfo(null);

      try {
        const response =
          await requestPublicStore(
            handleRetry
          );

        const payload =
          response?.data || {};

        if (
          payload?.status !== 'ok' ||
          !payload?.data
        ) {
          const error = new Error(
            payload?.message ||
              'La API devolvió una respuesta de tienda inválida.'
          );

          error.response = response;
          throw error;
        }

        if (!mounted) {
          return;
        }

        setTienda(payload.data);
        setStoreMeta(
          payload.store || null
        );
        setStoreError('');
        setRetryInfo(null);

        applyStoreDocumentData(
          payload.data
        );
      } catch (error) {
        if (!mounted) {
          return;
        }

        console.error(
          '[WebStore App] Error cargando información pública de la tienda:',
          error
        );

        setStoreError(
          getErrorMessage(error)
        );
      } finally {
        if (mounted) {
          setLoadingStore(false);
        }
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, []);

  /*
   * Si el navegador detecta que volvió la conectividad y la tienda quedó
   * en error, intentamos nuevamente sin exigir que el usuario recargue.
   */
  useEffect(() => {
    const handleOnline = () => {
      if (
        storeError &&
        !loadingStore
      ) {
        loadStore();
      }
    };

    window.addEventListener(
      'online',
      handleOnline
    );

    return () => {
      window.removeEventListener(
        'online',
        handleOnline
      );
    };
  }, [
    loadStore,
    loadingStore,
    storeError,
  ]);

  /* ===================================================================
   * ESTADOS INICIALES
   * ================================================================= */

  if (
    loadingStore &&
    !tienda
  ) {
    return (
      <LoadingStore
        retryInfo={retryInfo}
      />
    );
  }

  if (!tienda) {
    return (
      <StoreLoadError
        message={storeError}
        onRetry={loadStore}
        retrying={loadingStore}
      />
    );
  }

  /*
   * Se entregan varios nombres compatibles como props.
   * Los componentes que no los utilicen simplemente los ignoran.
   */
  const storeProps = {
    tienda,
    store: tienda,
    storeConfig: tienda,
    storeMeta,
  };

  /* ===================================================================
   * APP SHELL + RUTAS
   * ================================================================= */

  return (
    <ThemeProvider
      theme={storeTheme}
    >
      <CssBaseline />

      <div
        style={{
          ...styles.appShell,

          background:
            tienda?.branding
              ?.colorFondo ||
            '#f8fafc',

          color:
            tienda?.branding
              ?.colorTexto ||
            '#111827',
        }}
      >
        <SeoClient
          tienda={tienda}
          storeMeta={storeMeta}
        />

        <Header {...storeProps} />
        <Menu {...storeProps} />

        <main style={styles.main}>
          <Routes>
          <Route
            path="/"
            element={
              <Inicio {...storeProps} />
            }
          />

          <Route
            path="/productos"
            element={
              <Productos {...storeProps} />
            }
          />

          <Route
            path="/:slug"
            element={
              <PaginaDinamica
                {...storeProps}
              />
            }
          />

          <Route
            path="/producto/:slug"
            element={
              <ProductoDetalle
                {...storeProps}
              />
            }
          />

          <Route
            path="/marcas"
            element={
              <Marcas
                {...storeProps}
              />
            }
          />

          <Route
            path="/marca/:slug"
            element={
              <MarcaDetalles
                {...storeProps}
              />
            }
          />

          <Route
            path="/carrito"
            element={
              <Carrito {...storeProps} />
            }
          />

          <Route
            path="/contacto"
            element={
              <Contacto {...storeProps} />
            }
          />

          <Route
            path="/finalizar-compra"
            element={
              <FinalizarCompra
                {...storeProps}
              />
            }
          />

          <Route
            path="/seguimiento-pedido"
            element={
              <SeguimientoPedido
                {...storeProps}
              />
            }
          />

          <Route
            path="/categorias"
            element={<Categorias {...storeProps} />}
          />

          <Route
            path="/categoria"
            element={<Navigate to="/categorias" replace />}
          />

          <Route
            path="/categoria/:slug"
            element={<Categoria {...storeProps} />}
          />

          <Route
            path="*"
            element={
              <NoEncontrado
                {...storeProps}
              />
            }
          />
          </Routes>
        </main>

        <Footer {...storeProps} />
      </div>
    </ThemeProvider>
  );
}

/* =====================================================================
 * RESOLUCIÓN DEL HOST PRINCIPAL RETAIL
 * ---------------------------------------------------------------------
 * retail.integranube.cl es la vitrina comercial del vertical Retail.
 * También aceptamos el dominio pages.dev del proyecto y sus previews.
 *
 * IMPORTANTE:
 *  - En estos hosts NO montamos StoreAppContent.
 *  - Por lo tanto NO se ejecuta GET /tienda ni se intenta resolver tenant.
 *  - localhost conserva el flujo normal y utiliza DEV_STORE_DOMAIN desde
 *    api.js, por lo que el desarrollo local sigue mostrando una tienda.
 * =================================================================== */

function isRetailLandingHost() {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = String(window.location.hostname || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');

  return (
    hostname === 'retail.integranube.cl' ||
    hostname === 'webstore-retail.pages.dev' ||
    hostname.endsWith('.webstore-retail.pages.dev')
  );
}

function AppContent() {
  if (isRetailLandingHost()) {
    return <RetailLanding />;
  }

  return <StoreAppContent />;
}

/* =====================================================================
 * APP
 * ---------------------------------------------------------------------
 * Router se mantiene en App.js.
 * AppContent decide si mostrar la landing comercial o montar la WebStore
 * multi-tenant. StoreAppContent vive dentro del Router porque utiliza
 * useLocation().
 * =================================================================== */

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

/* =====================================================================
 * ESTILOS
 * =================================================================== */

const styles = {
  appShell: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },

  main: {
    flex: 1,
  },

  centerScreen: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: '#f8fafc',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },

  loaderCard: {
    width: '100%',
    maxWidth: '420px',
    textAlign: 'center',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '32px 28px',
    boxShadow:
      '0 12px 35px rgba(15, 23, 42, 0.08)',
  },

  errorCard: {
    width: '100%',
    maxWidth: '460px',
    textAlign: 'center',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '32px 28px',
    boxShadow:
      '0 12px 35px rgba(15, 23, 42, 0.08)',
  },

  spinner: {
    width: '38px',
    height: '38px',
    margin: '0 auto 18px',
    border: '4px solid #e5e7eb',
    borderTopColor: '#1976d2',
    borderRadius: '50%',
    animation:
      'webstore-spin 0.8s linear infinite',
  },

  warningIcon: {
    width: '42px',
    height: '42px',
    margin: '0 auto 18px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff7ed',
    color: '#c2410c',
    fontWeight: 800,
    fontSize: '24px',
  },

  loaderTitle: {
    color: '#111827',
    fontSize: '19px',
    lineHeight: 1.35,
    fontWeight: 700,
    marginBottom: '8px',
  },

  loaderText: {
    color: '#6b7280',
    fontSize: '14px',
    lineHeight: 1.6,
  },

  retryButton: {
    marginTop: '22px',
    appearance: 'none',
    border: 0,
    borderRadius: '10px',
    padding: '11px 20px',
    background: '#1976d2',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '14px',
    cursor: 'pointer',
  },
};

export default App;
